import { flatten } from 'ramda';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { sortPartialDate } from 'neuro-utils';
import { calculateDBSEffectFromDocuments } from 'neuro-calculation-commons';

import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import { Container, Item } from 'Components/Grid';
import UPDRSDialog from '../UPDRSDialog/updrsDialog';
import colors from '../../../../../config/theme/colors';
import { findLinkedDoc } from 'Routes/Updrs/utils';
import type { TRelevantDocumentType } from 'Routes/Updrs/utils';
import type { IDateSettingPair } from '../../../definitions';
import { formatPartialDate } from 'Utility/date';
import { mapDocumentToNeuroDocument, filterDocumentsByType } from 'Utility/documentHandling';

const settingOptionFormatter = (name: string | number, settings: Array<IDateSettingPair>): string =>
  flatten(settings.map(s => s.programs)).find(p => p?.id === name)?.name ?? '';

const DBSSettingsSelector = ({
  title,
  name,
  formData,
  viewing,
  options,
  settings,
}: {
  title: string;
  name: keyof IDBSEffect;
  formData: IFormData<IDocumentWithTypeV2<'dbsEffect'>>;
  viewing?: boolean;
  options: ISettingOptions[];
  settings: IDateSettingPair[];
}) => {
  const selectedId = formData.document[name];

  // Logic to find the date associated with the selected program ID
  const displayDate = React.useMemo(() => {
    if (!selectedId) return '';
    const match = settings.find(s => (s.programs ?? []).some(p => p.id === selectedId));
    return match?.settingDate ? formatPartialDate(match.settingDate) : '';
  }, [selectedId, settings]);

  // Don't render if no options and not in viewing mode
  if (options.length === 0 && !viewing) return null;

  return (
    <FormRow title={title} condition={options.length > 0 || !!viewing}>
      <Container>
        <Item style={{ justifyContent: 'flex-start', marginRight: '2rem' }}>
          <InputHandler
            type="SelectByCategory"
            editing={!viewing}
            name={name}
            formData={formData}
            options={options}
            optionFormatter={val => settingOptionFormatter(val, settings)}
            placeholder="updrs.chooseDbsSettings"
            fullWidth={true}
          />
        </Item>
        <Item style={{ justifyContent: 'flex-start', marginTop: '0.5rem', color: colors.secondaryText }}>
          {displayDate}
        </Item>
      </Container>
    </FormRow>
  );
};

const DBSEffectForm = ({ formData, documents, viewing, setSubDocuments }: IOwnProps): React.JSX.Element => {
  const { document, onChange } = formData;

  // 1. Data preparation
  const dbsDocs = React.useMemo(() => filterDocumentsByType(documents, ['dbs']), [documents]);

  const beforeDocObj = findLinkedDoc(documents, document.updrsIIIBeforeID);
  const afterDocObj = findLinkedDoc(documents, document.updrsIIIAfterID);

  // 2. Derived calculations
  const dbsSettings = React.useMemo<IDateSettingPair[]>(() => {
    const rawSettings = flatten(dbsDocs.map(d => d.settings));
    if (!rawSettings.length) return [];

    return rawSettings
      .map(s => ({
        settingDate: s?.generatorAdjustedDate,
        programs: (s?.programs ?? []).map(p => ({ id: p.id, name: p.programName })),
      }))
      .sort((a, b) => sortPartialDate(b.settingDate, a.settingDate));
  }, [dbsDocs]);

  const dbsSettingOptions = React.useMemo<ISettingOptions[]>(() => {
    return flatten(
      dbsSettings.map(s => ({
        title: formatPartialDate(s.settingDate),
        values: s.programs ? s.programs.map(p => p.id) : [],
      })),
    );
  }, [dbsSettings]);

  // 3. Effects

  // Handle testType defaults
  React.useEffect(() => {
    if (document.testType) return;

    const hasUpdrsDbsEffect = documents.some(
      doc => doc._type === 'dbsEffect' && doc.testType === 'UPDRS' && doc.type === 'dbs',
    );

    onChange && onChange({ testType: hasUpdrsDbsEffect ? 'UPDRS' : 'MDSUPDRS' });
  }, [document.testType, documents, onChange]);

  // Handle subdocument syncing and cleanup
  React.useEffect(() => {
    if (viewing) return;

    const editingDoc = documents.find(d => d._editing === true);
    const commitsLength = editingDoc?._commitsLength;
    if (!commitsLength) return;

    // Cleanup invalid IDs
    if (document.updrsIIIBeforeID && !beforeDocObj) {
      onChange?.({ updrsIIIBeforeID: undefined });
    }
    if (document.updrsIIIAfterID && !afterDocObj) {
      onChange?.({ updrsIIIAfterID: undefined });
    }

    // Update subdocuments
    const newSubDocs = [];
    if (beforeDocObj?._commitsLength && beforeDocObj._commitsLength >= commitsLength) {
      newSubDocs.push({ name: 'updrs_iii', id: document.updrsIIIBeforeID! });
    }
    if (afterDocObj?._commitsLength && afterDocObj._commitsLength >= commitsLength) {
      newSubDocs.push({ name: 'updrs_iii', id: document.updrsIIIAfterID! });
    }

    setSubDocuments?.(newSubDocs);
  }, [
    viewing,
    documents, // dependent on entire list to detect deletions
    document.updrsIIIBeforeID,
    document.updrsIIIAfterID,
    beforeDocObj,
    afterDocObj,
    setSubDocuments,
    onChange,
  ]);

  // 4. Score calculation
  const calculatedDBSEffect = React.useMemo(() => {
    const thisNeuroDoc = mapDocumentToNeuroDocument(document);
    const allNeuroDocs = documents.map(d => mapDocumentToNeuroDocument(d));
    return calculateDBSEffectFromDocuments(thisNeuroDoc, allNeuroDocs);
  }, [document, documents]);

  // 5. Render
  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="general.date">
          <InputHandler
            type="PartialDate"
            editing={!viewing}
            name="date"
            formData={formData}
            dateDefault="now"
            isNotCancellable={true}
          />
        </FormRow>
        <FormRow title="updrs.updrsOrMdsupdrs">
          <InputHandler
            type="Radio"
            name="testType"
            editing={!viewing}
            formData={formData}
            options={['UPDRS', 'MDSUPDRS']}
            optionFormatter={(n: string | number) => (
              <FormattedMessage id={`updrs.${n === 'UPDRS' ? 'updrsIII' : 'mds'}`} />
            )}
            disabledOptions={document.updrsIIIBeforeID || document.updrsIIIAfterID ? ['UPDRS', 'MDSUPDRS'] : []}
          />
        </FormRow>
        <DBSSettingsSelector
          title="updrs.dbsOldSettings"
          name="dbsOldSettings"
          formData={formData}
          viewing={viewing}
          options={dbsSettingOptions}
          settings={dbsSettings}
        />
        <FormRow title="updrs.updrsWithoutDBS">
          <UPDRSDialog
            name="updrsIIIBeforeID"
            parentDate={document.date}
            onChange={onChange}
            document={beforeDocObj}
            viewing={viewing}
            context={'dbs'}
            testType={document.testType ?? 'MDSUPDRS'}
          />
        </FormRow>
        <DBSSettingsSelector
          title="updrs.dbsNewSettings"
          name="dbsNewSettings"
          formData={formData}
          viewing={viewing}
          options={dbsSettingOptions}
          settings={dbsSettings}
        />
        <FormRow title="updrs.updrsWithDBS">
          <UPDRSDialog
            name="updrsIIIAfterID"
            parentDate={document.date}
            onChange={onChange}
            document={afterDocObj}
            viewing={viewing}
            context={'dbs'}
            retro={beforeDocObj?.retrospectiveData?.[0] === true}
            testType={document.testType ?? 'MDSUPDRS'}
          />
        </FormRow>
      </FormSection>
      <FormSection>
        <FormRow title="updrs.dbsEffectScore">
          {typeof calculatedDBSEffect === 'number' ? Math.round(calculatedDBSEffect * 100) / 100 + ' %' : '-'}
        </FormRow>
        <FormRow title="updrs.DBStestScoreManual">
          <InputHandler
            type="NumberField"
            editing={!viewing}
            name="manualScore"
            formData={formData}
            width={4.7}
            height={3}
            maxLength={5}
          />
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

interface ISettingOptions {
  title: string;
  values: string[];
}

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'dbsEffect'>>;
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[];
  viewing?: boolean;
  setSubDocuments?: (subDocuments: { name: string; id: string }[]) => void;
}

export default DBSEffectForm;
